import {memo, useEffect, useRef, useState} from "react";
import Hls from "hls.js";
import {getAssetPath} from "../../utils/get-asset-path.js";

const API_KEEPER = import.meta.env.VITE_API_KEEPER;
const NOT_FOUND_IMAGE = getAssetPath("img/vms-image-not-found.png");

const VmsPlayer = memo(({camera, className = ""}) => {
    const videoRef = useRef(null);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    const {cameraToken, streamUrl, url} = camera;
    const sourceUrl = streamUrl || (url ? API_KEEPER + url : "");

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return undefined;

        if (!sourceUrl) {
            setLoading(false);
            setFailed(true);
            return undefined;
        }

        let player;
        let retryTimeout;
        let cancelled = false;

        const connect = () => {
            if (cancelled) return;
            setLoading(true);

            if (Hls.isSupported()) {
                const options = {
                    enableWorker: true,
                    lowLatencyMode: true,
                    liveSyncDuration: 1,
                    liveMaxLatencyDuration: 5,
                    maxLiveSyncPlaybackRate: 2,
                };

                if (cameraToken) {
                    options.xhrSetup = (xhr) => xhr.setRequestHeader("Camera-Token", cameraToken);
                }

                player = new Hls(options);
                player.on(Hls.Events.MANIFEST_PARSED, () => video.play().catch(() => {}));
                player.on(Hls.Events.ERROR, (_event, data) => {
                    if (data.details !== Hls.ErrorDetails.BUFFER_STALLED_ERROR) setLoading(true);
                    if (data.fatal) {
                        player.destroy();
                        setLoading(false);
                        setFailed(true);
                        retryTimeout = setTimeout(connect, 1000);
                    }
                });
                player.loadSource(sourceUrl);
                player.attachMedia(video);
                return;
            }

            if (video.canPlayType("application/vnd.apple.mpegurl")) {
                video.src = sourceUrl;
                video.play().catch(() => {
                    setLoading(false);
                    setFailed(true);
                });
                return;
            }

            setLoading(false);
            setFailed(true);
        };

        const onPlaying = () => {
            setLoading(false);
            setFailed(false);
        };
        const onWaiting = () => setLoading(true);
        const onError = () => {
            setLoading(false);
            setFailed(true);
        };

        video.addEventListener("playing", onPlaying);
        video.addEventListener("waiting", onWaiting);
        video.addEventListener("error", onError);
        setFailed(false);
        connect();

        return () => {
            cancelled = true;
            clearTimeout(retryTimeout);
            player?.destroy();
            video.removeEventListener("playing", onPlaying);
            video.removeEventListener("waiting", onWaiting);
            video.removeEventListener("error", onError);
            video.removeAttribute("src");
            video.load();
        };
    }, [sourceUrl, cameraToken]);

    return (
        <div className={"relative flex h-full min-h-44 w-full items-center justify-center bg-[#e6e6e6] " + className}>
            <video className={"video-component" + (failed ? " invisible" : "")} ref={videoRef} muted playsInline/>

            {failed && <img src={NOT_FOUND_IMAGE} alt="Vídeo indisponível" className="absolute inset-0 h-full w-full object-contain"/>}

            {loading && !failed && (
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/30">
                    <div
                        className="h-10 w-10 animate-spin rounded-full border-4 border-solid border-current border-r-transparent text-white motion-reduce:animate-[spin_1.5s_linear_infinite]"
                        role="status"
                    />
                </div>
            )}
        </div>
    );
});

VmsPlayer.displayName = "VmsPlayer";

export default VmsPlayer;
